import { getApiBase } from '../../../../utils/apiConfig';

// API Gateway service endpoints (via Flask app)
const getServiceEndpoints = () => {
  
  // All requests go through the Flask API gateway. The base URL comes from the shared
  // apiConfig helper (which delegates to config.js) so there is one source of truth.
  const apiBase = getApiBase();
  
  // All environments use the same pattern: Flask app routes to Cloud Run services
  return {
    GEOCODING: `${apiBase}/v2/cloud-run`,
    ESRI: `${apiBase}/v2/cloud-run`,
    NEARBY_SCHOOLS: `${apiBase}/v2/cloud-run`,
    PROJECTIONS: `${apiBase}/v2/projections`,  // Backend service (not Cloud Run)
    METRICS: `${apiBase}/v2`,            // Backend service (not Cloud Run)
    TEAM: `${apiBase}/v2/admin`,         // Backend service (not Cloud Run)
    SPLITS: `${apiBase}/v2/splits`
  };
};

export const SERVICE_ENDPOINTS = getServiceEndpoints();

// Service configuration
export const SERVICE_CONFIG = {
  GEOCODING: {
    name: 'Geocoding Service',
    description: 'Converts addresses to coordinates',
    timeout: 30000, // 30 seconds
    retries: 3,
    test_mode_supported: true,
    supports_batch: true,
    icon: 'LocationOn',
    color: 'primary',
    service_type: 'cloud', // Cloud Run service
    required_fields: ['address'],
    optional_fields: ['city', 'state', 'zip', 'country']
  },
  ESRI: {
    name: 'ESRI Service',
    description: 'Fetches demographic and geographic data',
    timeout: 120000, // 2 minutes
    retries: 3,
    test_mode_supported: true,
    supports_batch: false,
    icon: 'Public',
    color: 'success',
    service_type: 'cloud', // Cloud Run service
    required_fields: ['latitude', 'longitude'],
    optional_fields: []
  },
  NEARBY_SCHOOLS: {
    name: 'Nearby Schools Service',
    description: 'Finds nearby schools within specified radius (test mode: calculate without storing)',
    timeout: 45000, // 45 seconds
    retries: 3,
    test_mode_supported: true,
    supports_batch: false,
    icon: 'NearMe',
    color: 'warning',
    service_type: 'cloud', // Cloud Run service
    required_fields: ['school_uuid'],
    optional_fields: ['drive_times', 'data_years'],
    test_description: 'Calculate nearby schools for a UUID without storing to database'
  },
  PROJECTIONS: {
    name: 'Projections Service',
    description: 'Generates enrollment projections (backend service)',
    timeout: 180000, // 3 minutes
    retries: 2,
    test_mode_supported: true,
    supports_batch: false,
    icon: 'TrendingUp',
    color: 'info',
    service_type: 'backend', // Backend service - no cloud environment dependencies
    required_fields: ['school_uuid'],
    optional_fields: ['projection_years'],
    test_description: 'Calculate projections for a UUID using backend processing'
  },
  METRICS: {
    name: 'Metrics Service',
    description: 'Calculates district metrics (backend service)',
    timeout: 180000, // 3 minutes
    retries: 2,
    test_mode_supported: true,
    supports_batch: false,
    icon: 'Analytics',
    color: 'error',
    service_type: 'backend', // Backend service (was Cloud Run, now local)
    required_fields: ['school_uuid'],
    optional_fields: ['drive_time'],
    test_description: 'Calculate metrics for a UUID using backend processing'
  },
  TEAM: {
    name: 'Team Management Service',
    description: 'Team and organizational management (backend service)',
    timeout: 30000, // 30 seconds
    retries: 3,
    test_mode_supported: true,
    supports_batch: false,
    icon: 'Group',
    color: 'default',
    service_type: 'backend', // Backend service
    required_fields: ['name'],
    optional_fields: ['funder', 'school_uuids']
  },
  SPLITS: {
    name: 'School Splits Service',
    description: 'Split school configuration and processing (test mode: validate without saving)',
    timeout: 120000, // 2 minutes
    retries: 2,
    test_mode_supported: true,
    supports_batch: true,
    icon: 'CallSplit',
    color: 'warning',
    service_type: 'backend', // Backend service
    required_fields: ['parent_ncessch'],
    optional_fields: ['split_config', 'addresses'],
    test_description: 'Test split configuration and validation without saving to database'
  }
};

// Available environments for monitoring
export const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
  LOCAL: 'local'
};

// Health check endpoints (now through Flask API gateway with multi-environment support)
export const HEALTH_CHECK_ENDPOINTS = {
  // Multi-environment endpoints
  ALL_ENVIRONMENTS: `${SERVICE_ENDPOINTS.GEOCODING}/health/environments`,
  ENVIRONMENT: (env) => `${SERVICE_ENDPOINTS.GEOCODING}/health/environment/${env}`,
  SERVICE_IN_ENVIRONMENT: (env, service) => `${SERVICE_ENDPOINTS.GEOCODING}/health/${env}/${service}`,
  
  // Backward compatible endpoints (default to staging)
  GEOCODING: `${SERVICE_ENDPOINTS.GEOCODING}/health/geocoding`,
  ESRI: `${SERVICE_ENDPOINTS.ESRI}/health/esri`,
  NEARBY_SCHOOLS: `${SERVICE_ENDPOINTS.NEARBY_SCHOOLS}/health/nearby_schools`,
  PROJECTIONS: `${SERVICE_ENDPOINTS.PROJECTIONS}/health`,  // Backend service health
  METRICS: `${SERVICE_ENDPOINTS.METRICS}/district/health`,
  TEAM: `${SERVICE_ENDPOINTS.TEAM}/health`,             // Team management health check
  SPLITS: `${SERVICE_ENDPOINTS.SPLITS}/health`,
  
  // Environment-specific endpoints
  DEVELOPMENT: {
    GEOCODING: `${SERVICE_ENDPOINTS.GEOCODING}/health/development/geocoding`,
    ESRI: `${SERVICE_ENDPOINTS.ESRI}/health/development/esri`,
    NEARBY_SCHOOLS: `${SERVICE_ENDPOINTS.NEARBY_SCHOOLS}/health/development/nearby_schools`
  },
  
  STAGING: {
    GEOCODING: `${SERVICE_ENDPOINTS.GEOCODING}/health/staging/geocoding`,
    ESRI: `${SERVICE_ENDPOINTS.ESRI}/health/staging/esri`,
    NEARBY_SCHOOLS: `${SERVICE_ENDPOINTS.NEARBY_SCHOOLS}/health/staging/nearby_schools`,
    METRICS: `${SERVICE_ENDPOINTS.METRICS}/district/health`
  },
  
  PRODUCTION: {
    GEOCODING: `${SERVICE_ENDPOINTS.GEOCODING}/health/production/geocoding`,
    ESRI: `${SERVICE_ENDPOINTS.ESRI}/health/production/esri`,
    NEARBY_SCHOOLS: `${SERVICE_ENDPOINTS.NEARBY_SCHOOLS}/health/production/nearby_schools`,
    METRICS: `${SERVICE_ENDPOINTS.METRICS}/district/health`
  }
};

// API paths for each service (now through Flask API gateway)
export const API_PATHS = {
  GEOCODING: {
    SYNC: '/geocode',
    ASYNC: '/geocode',
    JOBS: '/geocode',
    PROCESS: '/geocode', // Default for testing
    TEST: '/geocode' // Individual testing
  },
  ESRI: {
    SYNC: '/esri/demographics',
    PROCESS: '/esri/demographics', // Default for testing
    FETCH: '/esri/demographics',
    VALIDATE: '/esri/validate',
    TEST: '/esri/demographics'
  },
  NEARBY_SCHOOLS: {
    SEARCH: '/schools/nearby',
    PROCESS: '/nearby-schools/test', // Use test endpoint for UUID-based testing
    RADIUS: '/schools/nearby',
    VALIDATE: '/schools/nearby/validate',
    TEST: '/nearby-schools/test'
  },
  PROJECTIONS: {
    PROCESS: '/school',
    GENERATE: '/school',
    VALIDATE: '/school',
    TEST: '/test'
  },
  METRICS: {
    PROCESS: '/process/metrics',
    CALCULATE: '/process/metrics',
    VALIDATE: '/metrics/validate',
    TEST: '/metrics/test'
  },
  TEAM: {
    PROCESS: '/teams',
    ASSIGN: '/teams',
    VALIDATE: '/teams/search',
    TEST: '/teams'
  },
  SPLITS: {
    PROCESS: '/process',
    VALIDATE: '/validate',
    CONFIG: '/config',
    BULK: '/bulk',
    TEST: '/test'
  }
};

// Reprocessing endpoints
export const PROCESSING_ENDPOINTS = {
  REPROCESSING: '/v2/cloud-run/school/reprocess',
  STATUS: '/v2/cloud-run/school/status'
};

// Workflow endpoints for orchestrated processing
export const WORKFLOW_ENDPOINTS = {
  PROCESS: `${SERVICE_ENDPOINTS.GEOCODING}/workflow/process`,
  STATUS: `${SERVICE_ENDPOINTS.GEOCODING}/workflow/status`
};

// Helper function to get service configuration
export const getServiceConfig = (serviceKey) => {
  if (!serviceKey) return null;
  return SERVICE_CONFIG[serviceKey.toUpperCase()] || null;
}; 